import { IAction } from "../../../models/Action";
import { immutableSet } from "../../../utils/immutable";
import { actionTypes } from "./action";
import emptyState from "./emptyState";
import moment from "moment";
import { compose } from "redux";
import { IResponseState } from "../../../components/Modals/ResponseModal/module/constants";
import {
  CertificationItemType,
  IAddedApplicationItem,
  IAddedBrItem,
  IAddedSupervisorsItem,
  IAddedTechnicalUserItem,
  IAddedUsersItem,
  ICampaignCertificationInstance,
  ICampaignCertificationItem,
  ICampaignCertificationResponse,
  ICampaignRevisor,
  IConfigurationItem,
} from "../../../models/Recertification";
import {
  APPLICATION_IDENTIFIER,
  BUSINESS_ROLE_IDENTIFIER,
  CERTIFICATION_REFERENCE_IDENTIFIER,
  GROUP_IDENTIFIER,
  IEditPerimeterEntitlementItem,
  IEditPerimeterGroupItem,
  LIST_IDENTIFIER,
  LOADER_IDENTIFIER,
  OWNERSHIP_IDENTIFIER,
  SELECTED_ITEM,
  TECHNICAL_USER_IDENTIFIER,
  USER_IDENTIFIER,
} from "./constants";
import {
  IDENTITY_SYSTEM,
  IDENTITY_SYSTEM_LIST,
} from "../../../module/constants";

const {
  STEP_ONE,
  RAW_LIST,
  SELECTED_CERTIFICATION,
  STEP_TWO,
  STEP_THREE,
  CONFIRM_EXECUTION,
  CALL_STATE,
  REVISORS,
  CURRENT_USER,
  IS_ADMIN,
  IS_SUPERVISOR,
  GROUPS,
  EDIT_PERIMETER_GROUPS,
  EDIT_PERIMETER_OWNERSHIP,
  ADDED_GROUPS,
  TAB_SELECTED_IDENTIFIER,
  SUPERVISORS,
  ADDED_SUPERVISORS,
  ADDED_ENTITLEMENTS,
  ENTITLEMENTS,
  CONFIRM_MODAL_TRIGGER,
  EDIT_PERIMETER_USERS,
  USERS,
  ADDED_USERS,
  EDIT_PERIMETER_BRS,
  BR,
  ADDED_BR,
  EDIT_PERIMETER_APPLICATIONS,
  APPLICATIONS,
  ADDED_APPLICATIONS,
  EDIT_PERIMETER_TECHNICAL_USERS,
  TECHNICAL_USERS,
  ADDED_TECHNICAL_USERS,
  TABS_IDENTIFIER,
  USER_LIST_IDENTIFIER,
  OU_LIST_IDENTIFIER,
  BR_LIST_IDENTIFIER,
  ENTITLEMENT_LIST_IDENTIFIER,
  ACCOUNT_LIST_IDENTIFIER,
  TECHNICAL_USER_LIST_IDENTIFIER,
  APPLICATION_LIST_IDENTIFIER,
  ITEM_LIST_IDENTIFIER,
  EDIT_REVISOR,
  ITEM_IDENTIFIER,
  AGGREGATE_OU_ITEMS,
  AGGREGATE_BR_ITEMS,
  AGGREGATE_USER_ITEMS,
  AGGREGATE_ENTITLEMENTS_ITEMS,
  AGGREGATE_TECHNICAL_USERS_ITEMS,
  ACCESS_ROLE,
  CAMPAIGN_CONFIG,
  CAMPAIGN_REVISORS,
  CAMPAIGN_REVISORS_LOADER,
  IMPERSONIFICATION,
  ITEM_DETAIL,
} = CERTIFICATION_REFERENCE_IDENTIFIER;

/* ---------- SOD helpers -------------------------------------------------- */
const buildSodFields = (itemsSod?: any[]) => {
  const codes = (itemsSod ?? [])
      // some APIs send `conflictEntitlement`, others `conflictEntitlementName`
      .map((s: any) => s.conflictEntitlement ?? s.conflictEntitlementName ?? '')
      .filter(Boolean);

  return {
    /* <Table/> always needs this key – even when it’s empty */
    sod: codes.length ? codes.join(', ') : '', 
    involvedInSOD: codes.length > 0,
  };
};
/* ------------------------------------------------------------------------ */

const ACTION_HANDLERS = {
  [actionTypes.SET_CAMPAIGN_ACCESS_ROLE]: (state: any, action: any) =>
    immutableSet([ACCESS_ROLE], action.payload, state),
  [actionTypes.SET_IMPERSONIFICATION]: (state: any, action: any) =>
    immutableSet([IMPERSONIFICATION], action.payload, state),
  [actionTypes.SET_SELECTED_CERTIFICATION]: (state: any, action: any) => {
    return compose(immutableSet([SELECTED_CERTIFICATION], action.payload))(
      state
    );
  },
  [actionTypes.SELECT_ITEM_DETAIL]: (state: any, action: any) => {
    return compose(immutableSet([ITEM_DETAIL], action.payload))(state);
  },

  [actionTypes.GET_CAMPAIGN_REVISORS]: (state: any, action: any) =>
    immutableSet([STEP_TWO, CAMPAIGN_REVISORS_LOADER], true, state),

  [actionTypes.GET_CAMPAIGN_REVISORS_SUCCESS]: (state: any, action: any) => {
    const { revisors, ...paginationData } = action.payload;
    const mappedRevisors: ICampaignRevisor[] = revisors.map((revisor: any) => {
      const tempRev: ICampaignRevisor = {
        code: revisor.code,
        name: revisor.name,
        surname: revisor.surname,
        completedItems: revisor.completedItems,
        totalItems: revisor.totalItems,
        progress: `${revisor.completedItems}/${revisor.totalItems}`,
      };
      return tempRev;
    });
    const mappedResponse = { revisors: mappedRevisors, ...paginationData };
    return compose(
      immutableSet([STEP_TWO, CAMPAIGN_REVISORS], mappedResponse),
      immutableSet([STEP_TWO, CAMPAIGN_REVISORS_LOADER], false)
    )(state);
  },

  [actionTypes.GET_CAMPAIGN_REVISORS_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, CAMPAIGN_REVISORS], {}),
      immutableSet([STEP_TWO, CAMPAIGN_REVISORS_LOADER], false)
    )(state),

  [actionTypes.GET_CAMPAIGN_INFO_SUCCESS]: (state: any, action: any) => {
    let tempConfig: IConfigurationItem = action.payload.configuration;

    let temp = action.payload.supervisors;
    let tempArray = [] as IAddedSupervisorsItem[];
    if (temp.length > 0) {
      temp.forEach((user: any) => {
        let tempArrayItem: IAddedSupervisorsItem = {
          id_user: user.user.id,
          name: user.user.name,
          surname: user.user.surname,
          code: user.user.code,
          uidMaster: user.user.resp_code,
          organizational_unit: user.user.organizational_unit,
        };
        tempArray.push(tempArrayItem);
      });
    }
    let tempGroupsArray = [] as IEditPerimeterGroupItem[];
    let tempOwnershipsArray = [] as IEditPerimeterEntitlementItem[];
    let tempApplicationsArray = [] as IAddedApplicationItem[];
    let tempUsersArray = [] as IAddedUsersItem[];
    let tempBrsArray = [] as IAddedBrItem[];
    let tempTechnicalUserArray = [] as IAddedTechnicalUserItem[];
    if (action.payload.perimeter && action.payload.perimeter.length > 0) {
      action.payload.perimeter.forEach((perimeterObject: any) => {
        if (
          perimeterObject.type === GROUP_IDENTIFIER &&
          perimeterObject.ou?.ou
        ) {
          let tempGroupItem: IEditPerimeterGroupItem = {
            id: perimeterObject.ou.ou,
            name: perimeterObject.ou.name,
            ownercode: perimeterObject.ou.ownercode,
            identitySystemId:
              IDENTITY_SYSTEM[
                perimeterObject.ou
                  .identitySystemId as keyof typeof IDENTITY_SYSTEM
              ],
            include: perimeterObject.included,
          };
          tempGroupsArray.push(tempGroupItem);
        }
      });
      action.payload.perimeter.forEach((perimeterObject: any) => {
        if (
          perimeterObject.type === OWNERSHIP_IDENTIFIER &&
          perimeterObject.entitlement?.entitlement_id
        ) {
          let tempOwnershipsItem: IEditPerimeterEntitlementItem = {
            entitlement_id: perimeterObject.entitlement.entitlement_id,
            identity_system_id: perimeterObject.entitlement.identity_system_id,
            identitySystemId: perimeterObject.entitlement.identity_system_id,
            entitlement_name: perimeterObject.entitlement.entitlement_name,
            entitlement_description:
              perimeterObject.entitlement.entitlement_description,
            application_id: perimeterObject.entitlement.application_id,
            application_name: perimeterObject.entitlement.application_name,
            application_description:
              perimeterObject.entitlement.application_description,
            company: perimeterObject.entitlement.company,
            isCritical: perimeterObject.entitlement.isCritical,
            isMonoprofilo: perimeterObject.entitlement.isMonoprofilo,
            showOnFrontend: perimeterObject.entitlement.showOnFrontend,
            bao: perimeterObject.entitlement.bao,
            baoFullName: perimeterObject.entitlement.baoFullName,
            violationOfVisibility:
              perimeterObject.entitlement.violationOfVisibility,
            adminRoleRequired: perimeterObject.entitlement.adminRoleRequired,
            chainEntitlementsIds:
              perimeterObject.entitlement.chainEntitlementsIds,
            include: perimeterObject.included,
          };
          tempOwnershipsArray.push(tempOwnershipsItem);
        }
      });
      action.payload.perimeter.forEach((perimeterObject: any) => {
        if (
          perimeterObject.type === APPLICATION_IDENTIFIER &&
          perimeterObject.application?.id
        ) {
          let tempApplicationsItem: IAddedApplicationItem = {
            id: perimeterObject.application.id,
            application_name: perimeterObject.application.name,
            identitySystemId:
              IDENTITY_SYSTEM[
                perimeterObject.application
                  .identitySystemId as keyof typeof IDENTITY_SYSTEM
              ],
            include: perimeterObject.included,
          };
          tempApplicationsArray.push(tempApplicationsItem);
        }
      });
      action.payload.perimeter.forEach((perimeterObject: any) => {
        if (
          perimeterObject.type === BUSINESS_ROLE_IDENTIFIER &&
          perimeterObject.businessRole?.codeBusinessRole
        ) {
          let tempBrsItem: IAddedBrItem = {
            br_id: perimeterObject.businessRole.codeBusinessRole,
            name: perimeterObject.businessRole.name,
            description: perimeterObject.businessRole.description,
            ouCode: perimeterObject.businessRole.ou,
            isBR: true,
            entitlements: perimeterObject.businessRole.entitlements,
            include: perimeterObject.included,
          };
          tempBrsArray.push(tempBrsItem);
        }
      });
      action.payload.perimeter.forEach((perimeterObject: any) => {
        if (
          perimeterObject.type === USER_IDENTIFIER &&
          perimeterObject.user?.code
        ) {
          let tempUsersItem: IAddedUsersItem = {
            id_user: perimeterObject.user.code,
            name: perimeterObject.user.name,
            surname: perimeterObject.user.surname,
            uidMaster: perimeterObject.user.id,
            organizational_unit: perimeterObject.user.organizational_unit,
            include: perimeterObject.included,
          };
          tempUsersArray.push(tempUsersItem);
        }
      });
      action.payload.perimeter.forEach((perimeterObject: any) => {
        if (perimeterObject.type === TECHNICAL_USER_IDENTIFIER) {
          let tempTechnicalUserItem: IAddedTechnicalUserItem = {
            code: perimeterObject.technicalUser.tuCode,
            application: perimeterObject.technicalUser.applicationName,
            description: perimeterObject.technicalUser.description,
            include: perimeterObject.included,
            identitySystemId:
              IDENTITY_SYSTEM[
                perimeterObject.technicalUser
                  .identitySystemId as keyof typeof IDENTITY_SYSTEM
              ],
          };
          tempTechnicalUserArray.push(tempTechnicalUserItem);
        }
      });
    }
    return compose(
      immutableSet([CAMPAIGN_CONFIG], tempConfig),
      immutableSet([SUPERVISORS, ADDED_SUPERVISORS], tempArray),
      immutableSet([EDIT_PERIMETER_GROUPS, ADDED_GROUPS], tempGroupsArray),
      immutableSet(
        [EDIT_PERIMETER_OWNERSHIP, ADDED_ENTITLEMENTS],
        tempOwnershipsArray
      ),
      immutableSet(
        [EDIT_PERIMETER_APPLICATIONS, ADDED_APPLICATIONS],
        tempApplicationsArray
      ),
      immutableSet([EDIT_PERIMETER_BRS, ADDED_BR], tempBrsArray),
      immutableSet([EDIT_PERIMETER_USERS, ADDED_USERS], tempUsersArray),
      immutableSet(
        [EDIT_PERIMETER_TECHNICAL_USERS, ADDED_TECHNICAL_USERS],
        tempTechnicalUserArray
      )
    )(state);
  },
  [actionTypes.CERTIFICATION_REQUEST]: (state: any, action: any) =>
    immutableSet([STEP_ONE, LOADER_IDENTIFIER], true, state),
  [actionTypes.CERTIFICATION_REQUEST_SUCCESS]: (state: any, action: any) => {
    const { campaigns, ...paginationData } = action.payload;
    const tempRes: ICampaignCertificationItem[] = campaigns.map(
      (instance: ICampaignCertificationInstance) => {
        const {
          endDate,
          id,
          name,
          status,
          type,
          createdBy,
          totalItems,
          completedItems,
        } = instance;
        return {
          uniqueId: id,
          name: name,
          type: type === "OU" ? "ORGANIZATIONALUNIT" : type,
          endDate: moment(endDate).format("DD/MM/YYYY - HH:mm"),
          status: status,
          requestor: createdBy ? createdBy : "ND",
          progress: "[ " + completedItems + "/" + totalItems + " ]",
        } as ICampaignCertificationItem;
      }
    );

    const mappedResponse = { ...paginationData, campaigns: tempRes };

    return compose(
      immutableSet([STEP_ONE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_ONE, LIST_IDENTIFIER], mappedResponse),
      immutableSet([STEP_ONE, RAW_LIST], campaigns)
    )(state);
  },
  [actionTypes.CERTIFICATION_REQUEST_FAILURE]: (state: any, action: any) => {
    return compose(
      immutableSet([STEP_ONE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_ONE, LIST_IDENTIFIER], [])
    )(state);
  },

  [actionTypes.SELECT_ITEM]: (state: any, action: any) => {
    return immutableSet([STEP_THREE, SELECTED_ITEM], action.payload, state);
  },

  [actionTypes.SET_CONFIRM_EXECUTION]: (state: any, action: any) => {
    return immutableSet([CONFIRM_EXECUTION], action.payload, state);
  },

  [actionTypes.KEEP_ITEMS_REQUEST_SUCCESS]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.KEEP_ITEMS_REQUEST_FAILURE]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.REVOKE_ITEMS_REQUEST_SUCCESS]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.REVOKE_ITEMS_REQUEST_FAILURE]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.VALIDATE_ITEMS_REQUEST_SUCCESS]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.VALIDATE_ITEMS_REQUEST_FAILURE]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.FETCH_CLOSE_CERTIFICATION_SUCCESS]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.FETCH_CLOSE_CERTIFICATION_FAILURE]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.FETCH_CLOSE_CERTIFICATION_ITEM_SUCCESS]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.FETCH_CLOSE_CERTIFICATION_ITEM_FAILURE]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.FETCH_REOPEN_ITEM_SUCCESS]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.FETCH_REOPEN_ITEM_FAILURE]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.FETCH_SEND_CERTIFICATION_REMINDER]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.PENDING, state);
  },
  [actionTypes.FETCH_SEND_CERTIFICATION_REMINDER_SUCCESS]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.FETCH_SEND_CERTIFICATION_REMINDER_FAILURE]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },
  [actionTypes.FETCH_UPDATE_REVISOR_SUCCESS]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.FETCH_UPDATE_REVISOR_FAILURE]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },

  [actionTypes.UPDATE_AGGREGATE_OU_REVISOR_REQUEST]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.PENDING, state);
  },
  [actionTypes.UPDATE_AGGREGATE_OU_REVISOR_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.UPDATE_AGGREGATE_OU_REVISOR_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },

  [actionTypes.UPDATE_AGGREGATE_USER_REVISOR_REQUEST]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.PENDING, state);
  },
  [actionTypes.UPDATE_AGGREGATE_USER_REVISOR_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.UPDATE_AGGREGATE_USER_REVISOR_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },

  [actionTypes.UPDATE_AGGREGATE_BR_ENT_REVISOR_REQUEST]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.PENDING, state);
  },
  [actionTypes.UPDATE_AGGREGATE_BR_ENT_REVISOR_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.UPDATE_AGGREGATE_BR_ENT_REVISOR_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },

  [actionTypes.EDIT_CAMPAIGN_REQUEST]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.PENDING, state);
  },
  [actionTypes.EDIT_CAMPAIGN_REQUEST_SUCCESS]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.SUCCESS, state);
  },
  [actionTypes.EDIT_CAMPAIGN_REQUEST_FAILURE]: (state: any, action: any) => {
    return immutableSet([CALL_STATE], IResponseState.ERROR, state);
  },

  [actionTypes.GET_EDIT_REVISOR_REQUEST]: (state: any, action: any) =>
    immutableSet([STEP_THREE, REVISORS, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_EDIT_REVISOR_REQUEST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_THREE, REVISORS, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, REVISORS, LIST_IDENTIFIER], action.payload)
    )(state),
  [actionTypes.GET_EDIT_REVISOR_REQUEST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_THREE, REVISORS, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, REVISORS, LIST_IDENTIFIER], [])
    )(state),

  [actionTypes.RESET_STATE_STEP_ONE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, LOADER_IDENTIFIER], false),
      immutableSet([STEP_TWO, LIST_IDENTIFIER], []),
      immutableSet([SELECTED_CERTIFICATION], {}),
      immutableSet([IMPERSONIFICATION], null),
      immutableSet([ITEM_DETAIL], null),
      immutableSet([CAMPAIGN_CONFIG], {}),
      immutableSet([SUPERVISORS], {}),
      immutableSet([CONFIRM_EXECUTION], {
        type: "",
        certificationItemType: "",
        state: false,
        listIds: [],
      }),
      immutableSet([CALL_STATE], IResponseState.PENDING),
      immutableSet([STEP_THREE, SELECTED_ITEM], {})
    )(state),

  [actionTypes.SET_IS_CURRENT_USER_ADMIN]: (state: any, action: IAction) =>
    immutableSet([CURRENT_USER, IS_ADMIN], action.payload, state),
  [actionTypes.SET_IS_CURRENT_USER_SUPERVISOR]: (state: any, action: IAction) =>
    immutableSet([CURRENT_USER, IS_SUPERVISOR], action.payload, state),

  [actionTypes.EDIT_PERIMETER_GROUPS_PERIMETER_REQUEST]: (
    state: any,
    action: any
  ) =>
    immutableSet(
      [EDIT_PERIMETER_GROUPS, GROUPS, LOADER_IDENTIFIER],
      true,
      state
    ),
  [actionTypes.EDIT_PERIMETER_GROUPS_PERIMETER_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([EDIT_PERIMETER_GROUPS, GROUPS, LOADER_IDENTIFIER], false),
      immutableSet(
        [EDIT_PERIMETER_GROUPS, GROUPS, LIST_IDENTIFIER],
        action.payload
      )
    )(state),
  [actionTypes.EDIT_PERIMETER_GROUPS_PERIMETER_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([EDIT_PERIMETER_GROUPS, GROUPS, LOADER_IDENTIFIER], false),
      immutableSet([EDIT_PERIMETER_GROUPS, GROUPS, LIST_IDENTIFIER], [])
    )(state),
  [actionTypes.EDIT_PERIMETER_SET_ADDED_GROUPS_PERIMETER]: (
    state: any,
    action: any
  ) =>
    immutableSet([EDIT_PERIMETER_GROUPS, ADDED_GROUPS], action.payload, state),
  [actionTypes.EDIT_PERIMETER_SET_TAB_SELECTED]: (state: any, action: any) =>
    immutableSet([action.tab, TAB_SELECTED_IDENTIFIER], action.payload, state),

  [actionTypes.EDIT_PERIMETER_USERS_PERIMETER_REQUEST]: (
    state: any,
    action: any
  ) =>
    immutableSet([EDIT_PERIMETER_USERS, USERS, LOADER_IDENTIFIER], true, state),
  [actionTypes.EDIT_PERIMETER_USERS_PERIMETER_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([EDIT_PERIMETER_USERS, USERS, LOADER_IDENTIFIER], false),
      immutableSet(
        [EDIT_PERIMETER_USERS, USERS, LIST_IDENTIFIER],
        action.payload
      )
    )(state),
  [actionTypes.EDIT_PERIMETER_USERS_PERIMETER_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([EDIT_PERIMETER_USERS, USERS, LOADER_IDENTIFIER], false),
      immutableSet([EDIT_PERIMETER_USERS, USERS, LIST_IDENTIFIER], [])
    )(state),
  [actionTypes.EDIT_PERIMETER_SET_ADDED_USERS_PERIMETER]: (
    state: any,
    action: any
  ) => immutableSet([EDIT_PERIMETER_USERS, ADDED_USERS], action.payload, state),
  [actionTypes.EDIT_PERIMETER_USERS_SET_TAB_SELECTED]: (
    state: any,
    action: any
  ) =>
    immutableSet([action.tab, TAB_SELECTED_IDENTIFIER], action.payload, state),

  [actionTypes.EDIT_PERIMETER_BR_PERIMETER_REQUEST]: (
    state: any,
    action: any
  ) => immutableSet([EDIT_PERIMETER_BRS, BR, LOADER_IDENTIFIER], true, state),
  [actionTypes.EDIT_PERIMETER_BR_PERIMETER_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([EDIT_PERIMETER_BRS, BR, LOADER_IDENTIFIER], false),
      immutableSet([EDIT_PERIMETER_BRS, BR, LIST_IDENTIFIER], action.payload)
    )(state),
  [actionTypes.EDIT_PERIMETER_BR_PERIMETER_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([EDIT_PERIMETER_BRS, BR, LOADER_IDENTIFIER], false),
      immutableSet([EDIT_PERIMETER_BRS, BR, LIST_IDENTIFIER], [])
    )(state),
  [actionTypes.SET_ADDED_BR_PERIMETER]: (state: any, action: any) =>
    immutableSet([EDIT_PERIMETER_BRS, ADDED_BR], action.payload, state),

  [actionTypes.EDIT_PERIMETER_TECHNICAL_USER_PERIMETER_REQUEST]: (
    state: any,
    action: any
  ) =>
    immutableSet(
      [EDIT_PERIMETER_TECHNICAL_USERS, TECHNICAL_USERS, LOADER_IDENTIFIER],
      true,
      state
    ),
  [actionTypes.EDIT_PERIMETER_TECHNICAL_USER_PERIMETER_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [EDIT_PERIMETER_TECHNICAL_USERS, TECHNICAL_USERS, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [EDIT_PERIMETER_TECHNICAL_USERS, TECHNICAL_USERS, LIST_IDENTIFIER],
        action.payload
      )
    )(state),
  [actionTypes.EDIT_PERIMETER_TECHNICAL_USER_PERIMETER_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [EDIT_PERIMETER_TECHNICAL_USERS, TECHNICAL_USERS, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [EDIT_PERIMETER_TECHNICAL_USERS, TECHNICAL_USERS, LIST_IDENTIFIER],
        []
      )
    )(state),
  [actionTypes.SET_ADDED_TECHNICAL_USERS_PERIMETER]: (
    state: any,
    action: any
  ) =>
    immutableSet(
      [EDIT_PERIMETER_TECHNICAL_USERS, ADDED_TECHNICAL_USERS],
      action.payload,
      state
    ),
  [actionTypes.EDIT_PERIMETER_APPLICATIONS_PERIMETER_REQUEST]: (
    state: any,
    action: any
  ) =>
    immutableSet(
      [EDIT_PERIMETER_APPLICATIONS, APPLICATIONS, LOADER_IDENTIFIER],
      true,
      state
    ),
  [actionTypes.EDIT_PERIMETER_APPLICATIONS_PERIMETER_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [EDIT_PERIMETER_APPLICATIONS, APPLICATIONS, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [EDIT_PERIMETER_APPLICATIONS, APPLICATIONS, LIST_IDENTIFIER],
        action.payload
      )
    )(state),
  [actionTypes.EDIT_PERIMETER_APPLICATIONS_PERIMETER_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [EDIT_PERIMETER_APPLICATIONS, APPLICATIONS, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [EDIT_PERIMETER_APPLICATIONS, APPLICATIONS, LIST_IDENTIFIER],
        []
      )
    )(state),
  [actionTypes.SET_ADDED_APPLICATIONS_PERIMETER]: (state: any, action: any) =>
    immutableSet(
      [EDIT_PERIMETER_APPLICATIONS, ADDED_APPLICATIONS],
      action.payload,
      state
    ),
  [actionTypes.SET_TRIGGER_CONFIRM_MODAL_CERTIFICATION_REFERENCE]: (
    state: any,
    action: any
  ) => immutableSet([CONFIRM_MODAL_TRIGGER], action.payload, state),

  [actionTypes.SUPERVISORS_REQUEST_CERTIFICATION_REFERENCE]: (
    state: any,
    action: any
  ) => immutableSet([SUPERVISORS, SUPERVISORS, LOADER_IDENTIFIER], true, state),
  [actionTypes.SUPERVISORS_REQUEST_CERTIFICATION_REFERENCE_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([SUPERVISORS, SUPERVISORS, LOADER_IDENTIFIER], false),
      immutableSet([SUPERVISORS, SUPERVISORS, LIST_IDENTIFIER], action.payload)
    )(state),
  [actionTypes.SUPERVISORS_REQUEST_CERTIFICATION_REFERENCE_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([SUPERVISORS, SUPERVISORS, LOADER_IDENTIFIER], false),
      immutableSet([SUPERVISORS, SUPERVISORS, LIST_IDENTIFIER], [])
    )(state),
  [actionTypes.SET_ADDED_SUPERVISORS_CERTIFICATION_REFERENCE]: (
    state: any,
    action: any
  ) => immutableSet([SUPERVISORS, ADDED_SUPERVISORS], action.payload, state),

  [actionTypes.EDIT_PERIMETER_OWNERSHIP_SET_ADDED_ENTITLEMENTS_PERIMETER]: (
    state: any,
    action: any
  ) =>
    immutableSet(
      [EDIT_PERIMETER_OWNERSHIP, ADDED_ENTITLEMENTS],
      action.payload,
      state
    ),

  [actionTypes.EDIT_PERIMETER_OWNERSHIP_SET_TAB_SELECTED]: (
    state: any,
    action: any
  ) =>
    immutableSet([action.tab, TAB_SELECTED_IDENTIFIER], action.payload, state),

  [actionTypes.EDIT_PERIMETER_OWNERSHIP_ENTITLEMENTS_PERIMETER_REQUEST]: (
    state: any,
    action: any
  ) =>
    immutableSet(
      [EDIT_PERIMETER_OWNERSHIP, ENTITLEMENTS, LOADER_IDENTIFIER],
      true,
      state
    ),

  [actionTypes.EDIT_PERIMETER_OWNERSHIP_ENTITLEMENTS_PERIMETER_REQUEST_SUCCESS]:
    (state: any, action: any) => {
      return compose(
        immutableSet(
          [EDIT_PERIMETER_OWNERSHIP, ENTITLEMENTS, LOADER_IDENTIFIER],
          false
        ),
        immutableSet(
          [EDIT_PERIMETER_OWNERSHIP, ENTITLEMENTS, LIST_IDENTIFIER],
          action.payload
            .filter((entitlement: any) => entitlement.showOnFrontend)
            .map((entitlement: any) => {
              return {
                ...entitlement,
                identitySystemId:
                  IDENTITY_SYSTEM[
                    entitlement.identity_system_id as keyof typeof IDENTITY_SYSTEM
                  ],
              };
            })
        )
      )(state);
    },

  [actionTypes.EDIT_PERIMETER_OWNERSHIP_ENTITLEMENTS_PERIMETER_REQUEST_FAILURE]:
    (state: any, action: any) =>
      compose(
        immutableSet(
          [EDIT_PERIMETER_OWNERSHIP, ENTITLEMENTS, LOADER_IDENTIFIER],
          false
        ),
        immutableSet(
          [EDIT_PERIMETER_OWNERSHIP, ENTITLEMENTS, LIST_IDENTIFIER],
          []
        )
      )(state),

  [actionTypes.SET_TRIGGER_CONFIRM_MODAL_EDIT_PERIMETER]: (
    state: any,
    action: any
  ) => immutableSet([CONFIRM_MODAL_TRIGGER], action.payload, state),

  [actionTypes.GET_USER_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_USER_TAB_LIST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, USER_LIST_IDENTIFIER],
        action.payload
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_USER_TAB_LIST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([STEP_TWO, TABS_IDENTIFIER, USER_LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_OU_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_OU_TAB_LIST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, OU_LIST_IDENTIFIER],
        action.payload
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_OU_TAB_LIST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([STEP_TWO, TABS_IDENTIFIER, OU_LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_BR_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_BR_TAB_LIST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, BR_LIST_IDENTIFIER],
        action.payload
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_BR_TAB_LIST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([STEP_TWO, TABS_IDENTIFIER, BR_LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_ENTITLEMENT_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_ENTITLEMENT_TAB_LIST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, ENTITLEMENT_LIST_IDENTIFIER],
        action.payload
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_ENTITLEMENT_TAB_LIST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, ENTITLEMENT_LIST_IDENTIFIER],
        {}
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_ACCOUNT_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_ACCOUNT_TAB_LIST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, ACCOUNT_LIST_IDENTIFIER],
        action.payload
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_ACCOUNT_TAB_LIST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([STEP_TWO, TABS_IDENTIFIER, ACCOUNT_LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_TECHNICAL_USER_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_TECHNICAL_USER_TAB_LIST_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, TECHNICAL_USER_LIST_IDENTIFIER],
        action.payload
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_TECHNICAL_USER_TAB_LIST_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, TECHNICAL_USER_LIST_IDENTIFIER],
        {}
      ),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], [])
    )(state),

  [actionTypes.GET_ITEM_TAB_LIST]: (state: any, action: any) =>
    immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], true, state),

  [actionTypes.GET_ITEM_TAB_LIST_SUCCESS]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet(
        [STEP_TWO, TABS_IDENTIFIER, ITEM_LIST_IDENTIFIER],
        action.payload
      )
    )(state),

  [actionTypes.GET_ITEM_TAB_LIST_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_TWO, TABS_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([STEP_TWO, TABS_IDENTIFIER, ITEM_LIST_IDENTIFIER], {})
    )(state),

  [actionTypes.GET_ENTITLEMENTS_GIVEN_USER]: (state: any, action: any) =>
    immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_ENTITLEMENTS_GIVEN_USER_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        identitySystemId:
          IDENTITY_SYSTEM[
            item.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
        applicationName: item.applicationName,
        applicationDescription: item.applicationDescription,
        entitlementName: item.entitlementName,
        entitlementDescription: item.entitlementDescription,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.ENTITLEMENT,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_ENTITLEMENTS_GIVEN_USER_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.GET_USERS_GIVEN_ENTITLEMENT]: (state: any, action: any) =>
    immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_USERS_GIVEN_ENTITLEMENT_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        code: item.userCode,
        identitySystemId:
          IDENTITY_SYSTEM[
            item.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
        name: item.name,
        surname: item.surname,
        ou: item.ouName,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.USER,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_USERS_GIVEN_ENTITLEMENT_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.GET_ENTITLEMENTS_GIVEN_OU]: (state: any, action: any) =>
    immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_ENTITLEMENTS_GIVEN_OU_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        identitySystemId:
          IDENTITY_SYSTEM[
            item.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
        applicationName: item.applicationName,
        applicationDescription: item.applicationDescription,
        entitlementName: item.entitlementName,
        entitlementDescription: item.entitlementDescription,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.ENTITLEMENT,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_ENTITLEMENTS_GIVEN_OU_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.GET_ENTITLEMENTS_GIVEN_BR]: (state: any, action: any) =>
    immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_ENTITLEMENTS_GIVEN_BR_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        identitySystemId:
          IDENTITY_SYSTEM[
            item.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
        applicationName: item.applicationName,
        applicationDescription: item.applicationDescription,
        entitlementName: item.entitlementName,
        entitlementDescription: item.entitlementDescription,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.ENTITLEMENT,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_ENTITLEMENTS_GIVEN_BR_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.GET_TECHNICAL_USERS_GIVEN_ENTITLEMENT]: (
    state: any,
    action: any
  ) => immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_TECHNICAL_USERS_GIVEN_ENTITLEMENT_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        code: item.tuCode,
        application: item.applicationName,
        organizationalUnit: item.ouName,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.TECHNICAL_USER,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_TECHNICAL_USERS_GIVEN_ENTITLEMENT_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.GET_ENTITLEMENTS_GIVEN_TECHNICAL_USER]: (
    state: any,
    action: any
  ) => immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_ENTITLEMENTS_GIVEN_TECHNICAL_USER_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        identitySystemId:
          IDENTITY_SYSTEM[
            item.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
        applicationName: item.applicationName,
        applicationDescription: item.applicationDescription,
        entitlementName: item.entitlementName,
        entitlementDescription: item.entitlementDescription,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.ENTITLEMENT,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_ENTITLEMENTS_GIVEN_TECHNICAL_USER_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.GET_ACCOUNTS_GIVEN_USER]: (state: any, action: any) =>
    immutableSet([STEP_THREE, LOADER_IDENTIFIER], true, state),
  [actionTypes.GET_ACCOUNTS_GIVEN_USER_SUCCESS]: (state: any, action: any) => {
    let modifiedItems: any[] = [];
    let { items, ...paginationData } = action.payload;
    items.forEach((item: any) => {
      const baseSod = buildSodFields(item.itemsSod);
      let modifiedItem = {
        conflictEntitlement: baseSod.sod,
        itemId: item.itemId,
        identitySystemId:
          IDENTITY_SYSTEM[
            item.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
        user: item.accountCode,
        application: item.targetName,
        applicationDescription: item.targetDescription,
        status: item.status,
        revisor: item.revisorFull,
        involvedInSOD: (item.itemsSod ?? []).length > 0,
        action: item.action,
        revisorCode: item.revisorCode,
        hidedNote: item.notes,
        certificationItemTypology: CertificationItemType.ACCOUNT,
      };
      modifiedItems.push(modifiedItem);
    });
    let modifiedPayload = {
      ...paginationData,
      items: modifiedItems,
    };
    return compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], modifiedPayload),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state);
  },
  [actionTypes.GET_ACCOUNTS_GIVEN_USER_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([STEP_THREE, LOADER_IDENTIFIER], false),
      immutableSet([STEP_THREE, LIST_IDENTIFIER], {}),
      immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_BR_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_OU_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_USER_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS], []),
      immutableSet([EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS], []),
      immutableSet([CALL_STATE], IResponseState.PENDING)
    )(state),

  [actionTypes.MASSIVE_OPERATIONS_ITEM_REQUEST]: (state: any, action: any) =>
    immutableSet([CALL_STATE], IResponseState.PENDING, state),
  [actionTypes.MASSIVE_OPERATIONS_ITEM_REQUEST_SUCCESS]: (
    state: any,
    action: any
  ) => immutableSet([CALL_STATE], IResponseState.SUCCESS, state),
  [actionTypes.MASSIVE_OPERATIONS_ITEM_REQUEST_FAILURE]: (
    state: any,
    action: any
  ) => immutableSet([CALL_STATE], IResponseState.ERROR, state),

  [actionTypes.SET_EDIT_REVISOR_ITEM]: (state: any, action: any) => {
    return immutableSet([EDIT_REVISOR, ITEM_IDENTIFIER], action.payload, state);
  },
  [actionTypes.SET_EDIT_REVISOR_AGGREGATE_OU_ITEMS]: (
    state: any,
    action: any
  ) => {
    return immutableSet(
      [EDIT_REVISOR, AGGREGATE_OU_ITEMS],
      action.payload,
      state
    );
  },
  [actionTypes.SET_EDIT_REVISOR_AGGREGATE_BR_ITEMS]: (
    state: any,
    action: any
  ) => {
    return immutableSet(
      [EDIT_REVISOR, AGGREGATE_BR_ITEMS],
      action.payload,
      state
    );
  },
  [actionTypes.SET_EDIT_REVISOR_AGGREGATE_USER_ITEMS]: (
    state: any,
    action: any
  ) => {
    return immutableSet(
      [EDIT_REVISOR, AGGREGATE_USER_ITEMS],
      action.payload,
      state
    );
  },
  [actionTypes.SET_EDIT_REVISOR_AGGREGATE_ENTITLEMENTS_ITEMS]: (
    state: any,
    action: any
  ) => {
    return immutableSet(
      [EDIT_REVISOR, AGGREGATE_ENTITLEMENTS_ITEMS],
      action.payload,
      state
    );
  },
  [actionTypes.SET_EDIT_REVISOR_AGGREGATE_TECHNICAL_USERS_ITEMS]: (
    state: any,
    action: any
  ) => {
    return immutableSet(
      [EDIT_REVISOR, AGGREGATE_TECHNICAL_USERS_ITEMS],
      action.payload,
      state
    );
  },
};

export default function communicationReducer(
  state = emptyState,
  action: IAction
) {
  if (
    action &&
    action.type === "@@router/LOCATION_CHANGE" &&
    action.payload.location.pathname === "/welcome"
  ) {
    return emptyState;
  }
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
